import React from "react";
import parsha from "../assets/category-icons/parsha.svg";
import calendar from "../assets/category-icons/calendar.svg";
import tanya from "../assets/category-icons/tanya.svg";
import hayomyom from "../assets/category-icons/hayomyom.svg";
import maamarim from "../assets/category-icons/maamarim.svg";
import sichos from "../assets/category-icons/sichos.svg";
import tefilla from "../assets/category-icons/tefilla.svg";
import qa from "../assets/category-icons/qa.svg";
import glossaz from "../assets/category-icons/glossaz.svg";
import glossab from "../assets/category-icons/glossab.svg";
import talks from "../assets/category-icons/talks.svg";
import kabbalah from "../assets/category-icons/kabbalah.svg";
import rebbeim from "../assets/category-icons/rebbeim.svg";
import parshaSm from "../assets/category-icons/small/parsha.svg";
import hayomyomSm from "../assets/category-icons/small/hayomyom.svg";
import talksSm from "../assets/category-icons/small/talks.svg";
import tanyaSm from "../assets/category-icons/small/tanya.svg";
import maamarimSm from "../assets/category-icons/small/maamarim.svg";
import sichosSm from "../assets/category-icons/small/sichos.svg";
import tefillaSm from "../assets/category-icons/small/tefilla.svg";
import qaSm from "../assets/category-icons/small/qa.svg";
import rebbeimSm from "../assets/category-icons/small/rebbeim.svg";

/*
 * Category icon set — one illustration per top-level category in the
 * Categories Index manifest (CategoriesIndexPage `CATEGORIES`, keyed by
 * `key`). The SVGs are vector traces of the owner's reference sheets
 * (navy outline, pale-blue fills, royal-blue accent) meant to sit on a
 * soft blue disc. Served as static files (not inlined) so the paths stay
 * out of the JS bundle and cache on their own. The aleph-beis glossary
 * reuses the glossary artwork — the sheet has no separate icon for it.
 * `variant="small"` picks the simplified 24px cuts from the owner's
 * second sheet (nine of the thirteen); the rest fall back to the large
 * artwork, which still reads at that size.
 */

export const CATEGORY_ICONS = {
  parsha,
  calendar,
  tanya,
  hayomyom,
  maamarim,
  sichos,
  tefilla,
  qa,
  glossaz,
  glossab,
  talks,
  kabbalah,
  rebbeim,
};

export const CATEGORY_ICONS_SMALL = {
  parsha: parshaSm,
  hayomyom: hayomyomSm,
  talks: talksSm,
  tanya: tanyaSm,
  maamarim: maamarimSm,
  sichos: sichosSm,
  tefilla: tefillaSm,
  qa: qaSm,
  rebbeim: rebbeimSm,
};

/* Archive slug → icon key (the Categories Index manifest's slugs). */
export const CATEGORY_KEY_BY_SLUG = {
  "weekly-parsha": "parsha",
  "jewish-and-chassidic-calendar": "calendar",
  tanya: "tanya",
  "hayom-yom-2": "hayomyom",
  "maamarim-2": "maamarim",
  sichos: "sichos",
  "tefilla-prayer": "tefilla",
  qa: "qa",
  "chassidic-glossary": "glossaz",
  "chassidic-glossary-in-the-order-of-the-hebrew-aleph-beis": "glossab",
  "life-lessons": "talks",
  "thought-and-history": "kabbalah",
  "thought-and-history/23-lives-of-the-chabad-rebbeim": "rebbeim",
  "23-lives-of-the-chabad-rebbeim": "rebbeim",
};

/* Daily-study classes are often uncataloged — their titles say where they belong. */
const TITLE_KEYS = [
  [/chitas tanya/i, "tanya"],
  [/hayom yom/i, "hayomyom"],
];

/* Cross-cutting shelves: a class filed under Parsha or the Calendar usually
 * also lives in its subject category, which says more about it. */
const SECONDARY_KEYS = new Set(["parsha", "calendar"]);

/**
 * Icon key for a class from its ancestor slugs (nearest first — the first
 * slug with an icon wins, so a sub-category's own icon beats its root's),
 * then its title. Parsha / Calendar only win when nothing else matches.
 * null when nothing matches at all.
 */
export function categoryKeyForClass({ slugs = [], title = "" } = {}) {
  let secondary = null;
  for (const slug of slugs) {
    if (!slug) continue;
    const key = CATEGORY_KEY_BY_SLUG[slug] || CATEGORY_KEY_BY_SLUG[String(slug).split("/").pop()];
    if (!key) continue;
    if (!SECONDARY_KEYS.has(key)) return key;
    secondary = secondary || key;
  }
  const byTitle = TITLE_KEYS.find(([re]) => re.test(title || ""));
  return byTitle ? byTitle[1] : secondary;
}

/** Fallback for slugs outside the manifest — the sefarim shelf. */
export const CATEGORY_FALLBACK_ICON = maamarim;

export default function CategoryIcon({ categoryKey, size = 64, variant = "large", className = "", ...rest }) {
  const src =
    (variant === "small" && CATEGORY_ICONS_SMALL[categoryKey]) || CATEGORY_ICONS[categoryKey] || CATEGORY_FALLBACK_ICON;
  return (
    <img
      src={src}
      alt=""
      width={size}
      height={size}
      draggable="false"
      className={`block flex-shrink-0 select-none ${className}`}
      {...rest}
    />
  );
}

/** The soft-blue disc the reference sheet places every icon on. */
export function CategoryIconDisc({ categoryKey, size = 96, iconSize, className = "" }) {
  return (
    <span
      className={`inline-flex items-center justify-center rounded-full bg-[#EEF4FF] flex-shrink-0 ${className}`}
      style={{ width: size, height: size }}
    >
      <CategoryIcon categoryKey={categoryKey} size={iconSize || Math.round(size * 0.7)} />
    </span>
  );
}
